import { Stack } from '@mui/material';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Alert from '/@/components/bayer/alert';
import Spinner from '/@/components/spinner';
import { _ } from '/@/locale';
import { getCropConfig } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import type { Field } from '/@/types/field';
import { type Hybrid, PRECEON_BRAND_CODENAME } from '/@/views/recommendations/v2/steps/hybrids/hybrid-card';
import { useHybrids } from '/@/views/recommendations/v2/steps/hybrids/use-hybrids';
import { usePreselectedProduct } from '/@/views/recommendations/v2/steps/hybrids/use-preselected-product';
import ProductCard from './product-card';
import { selectSingleProduct } from './select-single-product';

/**
 * "Elegir producto" — the management recommendation's third step.
 *
 * The hybrid flow's counterpart ranks hybrids and takes several; this one takes exactly the
 * hybrid the grower already bought, so the two tabs collapse into a single list and selection
 * is a radio. It writes to the same `cropConfig.products`, which is what lets the management
 * step, the result page and the PDF stay shared between the two flows.
 */
function ProductStep({ field, crop = 'corn' }: { field: Field; crop?: 'corn' | 'soy' }) {
    const dispatch = useDispatch();
    const recommendation = useSelector(getActiveRecommendation);
    const cropConfig = getCropConfig(recommendation, crop);

    const { featuredProducts, otherProducts, isLoading } = useHybrids({ field, crop });
    // Ranked first, then the rest of the catalogue — one list, since the user is looking for a
    // specific hybrid rather than comparing recommendations.
    const products: Hybrid[] = useMemo(
        () => [...featuredProducts, ...otherProducts],
        [featuredProducts, otherProducts]
    );

    // One Preceon hybrid anywhere in the list indents every Preceon row by the ribbon's width;
    // the rest reserve the same gap so the whole column shares one left edge.
    const hasBrandRibbon = useMemo(
        () => products.some((product) => product.brand?.codename === PRECEON_BRAND_CODENAME),
        [products]
    );

    const selectedIds: number[] = cropConfig?.products || [];
    usePreselectedProduct({ offered: products, selectedIds });

    if (!cropConfig) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    const select = (id: number) =>
        dispatch(
            updateActiveRecommendationCropConfig({ key: 'products', value: selectSingleProduct(selectedIds, id) })
        );

    if (isLoading && !products.length) {
        return (
            <Stack alignItems='center' py={6}>
                <Spinner />
            </Stack>
        );
    }

    if (!products.length) {
        return (
            <Alert severity='surfaceVariant'>
                {_('No encontramos híbridos para este ambiente. Probá ajustando el ambiente del lote.')}
            </Alert>
        );
    }

    return (
        // The rows are a fixed column rather than the full content width: a hybrid name, two
        // chips and a yield don't fill 950px, and stretching them strands the yield far from
        // the name it belongs to. Narrower viewports shrink it instead of scrolling.
        <Stack spacing={2} pb={4} sx={{ width: '100%', maxWidth: { xs: 'unset', sm: 'max-content' } }}>
            {products.map((product) => (
                <ProductCard
                    key={product.id}
                    hybrid={product}
                    selected={selectedIds.includes(product.id)}
                    onSelect={() => select(product.id)}
                    reserveBrandRibbon={hasBrandRibbon}
                />
            ))}
        </Stack>
    );
}

export default ProductStep;
